import { EnrichedComponentCategory } from '../../domain/category/CategoryOperationDomains';
import { PageAction } from '../actions/PageActions';
import { ReduxBaseAction } from '../../domain/redux/ReduxBaseAction';
import { ReactNode } from 'react';
import ComponentName from '../../constant/ComponentName';
import FolderService from '../../service/FolderService';
import { CustomFilter, CustomFilterFields } from '../../domain/ui/FilterDomains';
import ValueFactory from '../../util/ValueFactory';
import LanguageUtil from '../../util/LanguageUtil';

export interface PageState {
  sidebarCollapsed: boolean;
  hamburgerMenuToggle: boolean;
  itemView: 'gallery' | 'list';
  pagePath: PagePath[];
  activePageIndex: number;
  currentCategory: EnrichedComponentCategory | undefined;
  currentComponent: ComponentName | undefined;
  enrichedCategories: Record<string, EnrichedComponentCategory>;
  lastComponent: ComponentName | undefined;
  lastCategory: EnrichedComponentCategory | undefined;
  lastEnrichedCategories: Record<string, EnrichedComponentCategory>;
  language: string;
  filter: {
    filter: CustomFilter;
    items: CustomFilterFields[];
  };
  filters: Record<string, this['filter']>;
  metricIntervals: number[];
}

export type PagePathType = 'category' | 'component' | 'componentId';

export interface PagePath {
  title: string;
  type: PagePathType;
  icon?: ReactNode;
  path?: string;
  payload: EnrichedComponentCategory | any;
  [x: string]: any;
}

const initialState: PageState = {
  sidebarCollapsed: false,
  hamburgerMenuToggle: false,
  itemView: 'list',
  pagePath: [],
  activePageIndex: 0,
  currentCategory: undefined,
  lastCategory: undefined,
  enrichedCategories: {},
  language: 'en-US',
  currentComponent: undefined,
  lastEnrichedCategories: {},
  lastComponent: undefined,
  filter: {
    filter: ValueFactory.initializeFilter(),
    items: [],
  },
  metricIntervals: [],
  filters: {},
};

export const initialPagePath: Record<string, any> = {
  [ComponentName.TRANSACTION]: {
    get title() {
      return LanguageUtil.get({ id: 'common.label.channel' });
    },
    path: 'transaction',
  },
  [ComponentName.ENTITY]: {
    get title() {
      return LanguageUtil.get({ id: 'common.label.entity' });
    },
    path: 'entity',
  },
  [ComponentName.CONNECTION]: {
    get title() {
      return LanguageUtil.get({ id: 'common.label.connection' });
    },
    path: 'connection',
  },
  [ComponentName.CASE]: {
    get title() {
      return LanguageUtil.get({ id: 'common.label.transaction' });
    },
    path: 'cases',
  },
  [ComponentName.CONFIGURATION]: {
    get title() {
      return LanguageUtil.get({ id: 'common.label.transactionConfiguration' });
    },
    path: 'configuration',
  },
  [ComponentName.ENRICHMENT]: {
    get title() {
      return LanguageUtil.get({ id: 'common.label.enrichment' });
    },
    path: 'enrichment',
  },
  [ComponentName.RULE]: {
    get title() {
      return LanguageUtil.get({ id: 'common.label.rule' });
    },
    path: 'rule',
  },
  [ComponentName.LIST]: {
    get title() {
      return LanguageUtil.get({ id: 'common.label.list' });
    },
    path: 'list',
  },
  [ComponentName.STREAM]: {
    get title() {
      return LanguageUtil.get({ id: 'common.label.store' });
    },
    path: 'stream',
  },
  [ComponentName.STRUCT]: {
    get title() {
      return LanguageUtil.get({ id: 'common.label.struct' });
    },
    path: 'struct',
  },
  [ComponentName.ACTION]: {
    get title() {
      return LanguageUtil.get({ id: 'common.label.action' });
    },
    path: 'action',
  },
  [ComponentName.ACCOUNT]: {
    get title() {
      return LanguageUtil.get({ id: 'common.label.profile' });
    },
    path: 'account',
  },
  [ComponentName.ADMINISTRATION]: {
    get title() {
      return LanguageUtil.get({ id: 'common.label.administration' });
    },
    path: 'admin',
  },
  [ComponentName.SANDBOX]: {
    get title() {
      return LanguageUtil.get({ id: 'common.label.sandbox' });
    },
    path: 'sandbox',
  },
  [ComponentName.DASHBOARD]: {
    get title() {
      return LanguageUtil.get({ id: 'common.label.dashboard' });
    },
    path: 'dashboard',
  },
  [ComponentName.WATCHLIST]: {
    get title() {
      return LanguageUtil.get({ id: 'common.label.watchlist' });
    },
    path: 'watchlist',
  },
  [ComponentName.CUSTOM_LIST]: {
    get title() {
      return LanguageUtil.get({ id: 'common.label.customList', defaultMessage: 'Custom List' });
    },
    path: 'custom-list',
  },
  [ComponentName.WATCHLIST_ALERT]: {
    get title() {
      return LanguageUtil.get({ id: 'common.label.alert' });
    },
    path: 'watchlist-alert',
  },
  [ComponentName.GRAPH]: {
    get title() {
      return LanguageUtil.get({ id: 'common.label.graph' });
    },
    path: 'chart',
  },
  [ComponentName.CHART]: {
    get title() {
      return LanguageUtil.get({ id: 'common.label.chart' });
    },
    path: 'chart',
  },
  [ComponentName.AI_MODEL]: {
    get title() {
      return LanguageUtil.get({ id: 'common.label.aiModel' });
    },
    path: 'ai-model',
  },
  [ComponentName.TRACKER]: {
    get title() {
      return LanguageUtil.get({ id: 'common.label.tracker' });
    },
    path: 'tracker',
  },
  [ComponentName.POLICY]: {
    get title() {

      return LanguageUtil.get({id: "common.label.policy"})

    }, path: "policy",
  },
  [ComponentName.ASSESSMENT]: {
    get title() {

      return LanguageUtil.get({id: "common.label.assessment"})
    }, path: "assessment",
  },
  [ComponentName.ACH]: {
    get title() {
      return LanguageUtil.get({ id: 'pages.ach.title', defaultMessage: 'Automated Clearing House' });
    },
    path: 'ach',
  }
};

export const pageReducer = (state = initialState, action: ReduxBaseAction<any, PageAction>) => {
  switch (action.type) {
    case 'CHANGE_SIDEBAR_COLLAPSE': {
      return {
        ...state,
        sidebarCollapsed: action.payload,
      };
    }
    case 'CHANGE_HAMBURGER_MENU_TOGGLE':
      return {
        ...state,
        hamburgerMenuToggle: action.payload,
      };
    case 'CHANGE_ITEM_VIEW': {
      return {
        ...state,
        itemView: action.payload,
      };
    }
    case 'CHANGE_PAGE_PATH': {
      return {
        ...state,
        pagePath: action.payload,
        activePageIndex: action.payload.length - 1,
      };
    }
    case 'ADD_PAGE_PATH': {
      const pagePath = [...state.pagePath, action.payload];
      return {
        ...state,
        pagePath,
        activePageIndex: pagePath.length - 1,
      };
    }
    case 'CHANGE_CURRENT_CATEGORY':
      return {
        ...state,
        currentCategory: action.payload,
        lastCategory: action.payload,
      };
    case 'CHANGE_CURRENT_COMPONENT':
      if (state.lastComponent != undefined && state.lastComponent != action.payload) {
        state.lastEnrichedCategories = {};
        state.lastCategory = undefined;
      }
      return {
        ...state,
        currentComponent: action.payload,
        lastComponent: action.payload,
      };
    case 'CHANGE_ENRICHED_CATEGORIES':
      return {
        ...state,
        enrichedCategories: action.payload,
        lastEnrichedCategories: action.payload,
      };
    case 'CHANGE_ACTIVE_PAGE_INDEX':
      return {
        ...state,
        activePageIndex: action.payload,
      };
    case 'NEXT_PAGE':
      if (state.pagePath.length > state.activePageIndex + 1) {
        return {
          ...state,
          activePageIndex: state.activePageIndex + 1,
        };
      }
      return state;
    case 'PREVIOUS_PAGE':
      if (state.activePageIndex - 1 >= 0 && state.pagePath.length > state.activePageIndex) {
        return {
          ...state,
          activePageIndex: (state.activePageIndex -= 1),
        };
      }
      return { ...state };
    case 'CLEAR_PAGE':
      return { ...state, enrichedCategories: {}, currentCategory: undefined, filter: { filter: ValueFactory.initializeFilter(), items: [] }, filters: {} };
    case 'SET_LANGUAGE':
      return {
        ...state,
        language: action.payload,
      };
    case 'CALCULATE_PAGE_PATH':
      if (action.payload.disablePathCalculation == undefined || !action.payload.disablePathCalculation) {
        const pathname = window.location.pathname.split('/');
        pathname.shift();
        pathname.shift();
        const path = pathname.join('/');
        if (action.payload.id) {
          FolderService.calculate('UPDATE', action.payload.component, path, state, action.payload.id);
        } else {
          FolderService.calculate('CREATE', action.payload.component, path, state);
        }
      }
      return { ...state };
    case 'CHANGE_PAGE_FILTER':
      return {
        ...state,
        filter: action.payload,
      };
    case 'CHANGE_PAGE_FILTERS':
      return {
        ...state,
        filters: action.payload,
      };
    case 'CHANGE_METRIC_INTERVALS':
      return {
        ...state,
        metricIntervals: action.payload,
      };
    default:
      return state;
  }
};
